// Microbe v2 — the always-on DOM overlay (HUD).
//
// Sits above the full-screen #stage canvas. Shows the live wallet (energy/biomass/xp/enzymes with
// per-second rates for the two idle currencies), a growth-to-division progress bar with a LOCKED
// teaser pill when full, a sound toggle, and the big GENOME button that opens the genome sheet.
//
// Reads everything through GameCtx (never the save shape directly). Repaints on ctx.onChange AND on
// a cheap ~4x/sec interval so the idle rate/values stay live even between state-change events.

import { divideProgress } from './state.js';
import type { GameCtx } from './types.js';

/** Compact number formatter — 999 -> "999", 1_234 -> "1.2k", 3_400_000 -> "3.4M". */
function fmt(n: number): string {
  const v = Math.floor(n);
  if (v < 1000) return String(v);
  if (v < 1_000_000) return trim(v / 1000) + 'k';
  if (v < 1_000_000_000) return trim(v / 1_000_000) + 'M';
  return trim(v / 1_000_000_000) + 'B';
}
/** One decimal, but drop a trailing ".0" (1.0 -> "1", 1.2 -> "1.2"). */
function trim(x: number): string {
  const s = x.toFixed(1);
  return s.endsWith('.0') ? s.slice(0, -2) : s;
}
/** Per-second rate readout — one decimal, always signed positive. */
function rate(n: number): string {
  return '+' + n.toFixed(1) + '/s';
}

/**
 * Build the HUD once into `host`, wire the GENOME + sound controls, and return a { render } the
 * caller (main.ts) can also invoke on demand. render() is idempotent — it only writes text/styles
 * into the nodes created here.
 */
export function mountHud(
  host: HTMLElement,
  ctx: GameCtx,
  onOpenGenome: () => void,
): { render(): void } {
  host.classList.add('hud');
  host.innerHTML = '';

  // ---- top: wallet strip ----
  const wallet = document.createElement('div');
  wallet.className = 'hud-wallet';

  // energy / biomass carry a live per-second rate; xp / enzymes are act-only, no idle rate.
  const energyVal = walletCell(wallet, '⚡', true);
  const biomassVal = walletCell(wallet, '🧫', true);
  const xpVal = walletCell(wallet, '✨', false);
  const enzymesVal = walletCell(wallet, '🧪', false);

  // ---- sound toggle ----
  const soundBtn = document.createElement('button');
  soundBtn.id = 'soundBtn';
  soundBtn.type = 'button';
  soundBtn.setAttribute('aria-label', 'toggle sound');
  // Behaviour is wired in main.ts (it owns the persist + audio.setEnabled + notify), so the toggle
  // fans out through ctx.onChange to every surface. The HUD only mints the button and repaints its
  // icon in render() below; wiring a second click handler here would double-toggle back to no-op.

  // ---- growth-to-division bar + locked teaser pill ----
  const growthbar = document.createElement('div');
  growthbar.className = 'growthbar';
  const growthFill = document.createElement('i');
  growthbar.appendChild(growthFill);

  const dividePill = document.createElement('span');
  dividePill.id = 'dividePill';
  dividePill.textContent = 'Divide 🔒 soon';
  dividePill.hidden = true;

  // ---- bottom: GENOME button ----
  const genomeBtn = document.createElement('button');
  genomeBtn.id = 'genomeBtn';
  genomeBtn.type = 'button';
  genomeBtn.textContent = 'GENOME';
  genomeBtn.addEventListener('click', () => {
    ctx.haptic(10);
    onOpenGenome();
  });

  host.append(wallet, soundBtn, growthbar, dividePill, genomeBtn);

  function render(): void {
    const w = ctx.wallet();
    const r = ctx.rates();

    energyVal.value.textContent = fmt(w.energy);
    biomassVal.value.textContent = fmt(w.biomass);
    xpVal.value.textContent = fmt(w.xp);
    enzymesVal.value.textContent = fmt(w.enzymes);

    if (energyVal.rate) energyVal.rate.textContent = rate(r.energy);
    if (biomassVal.rate) biomassVal.rate.textContent = rate(r.biomass);

    const p = divideProgress();
    growthFill.style.width = (p * 100).toFixed(1) + '%';
    const full = p >= 1;
    growthbar.classList.toggle('full', full);
    dividePill.hidden = !full;

    soundBtn.textContent = ctx.state().soundOn ? '🔊' : '🔈';
    soundBtn.classList.toggle('off', !ctx.state().soundOn);
  }

  // repaint on any state change...
  const unsub = ctx.onChange(render);
  // ...and ~4x/sec so idle accrual + rates read live between events (cheap text-only writes).
  const timer = window.setInterval(render, 250);

  // best-effort teardown when the host leaves the DOM (SPA never unmounts in practice; harmless).
  window.addEventListener('pagehide', () => {
    window.clearInterval(timer);
    unsub();
  });

  render();
  return { render };
}

/** One wallet cell: icon + value, optionally a per-second rate line. Returns its live nodes. */
function walletCell(
  parent: HTMLElement,
  icon: string,
  withRate: boolean,
): { value: HTMLElement; rate: HTMLElement | null } {
  const cell = document.createElement('div');
  cell.className = 'hud-coin';

  const ico = document.createElement('span');
  ico.className = 'ico';
  ico.textContent = icon;

  const stack = document.createElement('span');
  stack.className = 'stack';

  const value = document.createElement('b');
  value.textContent = '0';
  stack.appendChild(value);

  let rateEl: HTMLElement | null = null;
  if (withRate) {
    rateEl = document.createElement('small');
    rateEl.className = 'hud-rate';
    rateEl.textContent = '+0.0/s';
    stack.appendChild(rateEl);
  }

  cell.append(ico, stack);
  parent.appendChild(cell);
  return { value, rate: rateEl };
}
